declare global {
  interface Window {
    logs: string[]
  }
}

const MAX_LOGS = 500

/**
 * Native reads `window.logs.join('\n')` when a WebView loading timeout fires
 * and reports it as the `logs` custom key of `web_view_timeout_error`, where
 * Crashlytics keeps only the first 1024 characters. The breadcrumb line is
 * therefore always `window.logs[0]`, and capped so the errors after it still
 * get most of that budget.
 */
const BREADCRUMB_PREFIX = '[bc]'
const MAX_BREADCRUMB_LINE = 280
const MAX_DETAIL = 40
const BREADCRUMB_OVERFLOW = '…'

// Server-side renders and node-environment tests import this through
// @forest/native-services too: without a DOM it records nothing. A node test
// may stub `window` with just `location` (focus-pass), so check for the APIs
// used here rather than for `window` alone.
const inBrowser =
  typeof window !== 'undefined' &&
  typeof window.addEventListener === 'function' &&
  typeof document !== 'undefined'

if (inBrowser) window.logs = []

let breadcrumbLine = ''
let breadcrumbFull = false

const ms = (at: number) => Math.round(at)

/**
 * Appends one `code:time` token to the boot breadcrumb line, where time is ms
 * since navigation start (`performance.now()`) and detail, when given,
 * follows after a slash. The line stops growing at its cap and marks the cut.
 *
 * For a handful of boot milestones only — it is not a log. Errors still go
 * through `console.error`.
 */
export function breadcrumb(code: string, detail?: string | number): void {
  if (!inBrowser || breadcrumbFull) return
  const suffix =
    detail === undefined ? '' : `/${String(detail).slice(0, MAX_DETAIL)}`
  const base = breadcrumbLine || BREADCRUMB_PREFIX
  let next = `${base} ${code}:${ms(performance.now())}${suffix}`
  if (next.length > MAX_BREADCRUMB_LINE) {
    breadcrumbFull = true
    next = `${base} ${BREADCRUMB_OVERFLOW}`
  }
  writeBreadcrumbLine(next)
}

function writeBreadcrumbLine(next: string) {
  const logs = window.logs
  const index = breadcrumbLine ? logs.indexOf(breadcrumbLine) : -1
  breadcrumbLine = next
  if (index === 0) {
    logs[0] = next
    return
  }
  // Something trimmed or reordered the array: put the line back in front.
  if (index > 0) logs.splice(index, 1)
  logs.unshift(next)
}

function format(args: unknown[]): string {
  return args
    .map((a) => {
      if (typeof a === 'string') return a
      if (a instanceof Error) return `${a.name}: ${a.message}\n${a.stack ?? ''}`
      try {
        return JSON.stringify(a)
      } catch {
        return String(a)
      }
    })
    .join(' ')
}

function push(level: string, message: string) {
  const logs = window.logs
  logs.push(`[${level}] ${message}`)
  if (logs.length > MAX_LOGS) {
    // Drop the oldest entry after the breadcrumb line, never the line itself.
    logs.splice(breadcrumbLine && logs[0] === breadcrumbLine ? 1 : 0, 1)
  }
}

if (inBrowser) installDiagnostics()

function installDiagnostics() {
  // Only `error`. Native's JS bridge console.logs every call and callback
  // (`add callback: …` / `onCallback: …`), which would bury everything else
  // under the 1024-character cut.
  const levels = ['error'] as const

  for (const level of levels) {
    const original = console[level].bind(console)
    console[level] = (...args: unknown[]) => {
      push(level, format(args))
      original(...args)
    }
  }

  window.addEventListener('error', (event) => {
    push(
      'uncaught',
      `${event.message} @ ${event.filename}:${event.lineno}:${event.colno}`,
    )
  })

  window.addEventListener('unhandledrejection', (event) => {
    const reason = event.reason
    const message =
      reason instanceof Error
        ? `${reason.name}: ${reason.message}\n${reason.stack ?? ''}`
        : format([reason])
    push('unhandledrejection', message)
  })

  // Boot milestones visible from here. The bridge ones (handshake, pageReady,
  // pageVisible) come from @forest/native-services.
  //   b         this bundle started running (≈ HTML + JS downloaded); /h if hidden
  //   dcl       DOMContentLoaded — native injects its Bifrost shim around here
  //   ld        window load
  //   hid, vis  page visibility changes (a hidden page's timers are throttled)
  //   gap       a 1 s timer fired late, /<ms late> (timers paused or throttled)
  breadcrumb('b', document.visibilityState === 'hidden' ? 'h' : undefined)

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => breadcrumb('dcl'), {
      once: true,
    })
  } else {
    breadcrumb('dcl', 'early')
  }
  if (document.readyState === 'complete') {
    breadcrumb('ld', 'early')
  } else {
    window.addEventListener('load', () => breadcrumb('ld'), { once: true })
  }

  document.addEventListener('visibilitychange', () => {
    breadcrumb(document.visibilityState === 'hidden' ? 'hid' : 'vis')
  })

  // Watch timer lateness through the first 30 s (native's loading deadline is
  // 15 s). A late tick means this page's timers were paused or throttled, which
  // stalls every bridge deadline measured with them too.
  const GAP_TICK_MS = 1000
  const GAP_THRESHOLD_MS = 1500
  const GAP_WATCH_UNTIL_MS = 30_000

  function scheduleGapTick() {
    const expectedAt = performance.now() + GAP_TICK_MS
    setTimeout(() => {
      const now = performance.now()
      const late = now - expectedAt
      if (late > GAP_THRESHOLD_MS) breadcrumb('gap', ms(late))
      if (now < GAP_WATCH_UNTIL_MS) scheduleGapTick()
    }, GAP_TICK_MS)
  }
  scheduleGapTick()
}

export type WindowLogs = Window['logs']
